/* eslint-disable camelcase */
import {
  useState, useEffect, useRef, useMemo,
} from 'react';
import GoalServices from '../../../services/goal.services';

/**
 * Custom field type IDs from the API
 * Matches CustomAttributeFieldType enum from backend
 */
export const CustomAttributeFieldType = {
  TEXT_FIELD: 1,
  TEXT_AREA: 2,
  DROPDOWN: 3,
  MULTI_SELECT: 4,
}

const SUPPORTED_CUSTOM_FIELD_TYPES = [
  CustomAttributeFieldType.TEXT_FIELD, // TEXT_AREA will be converted to TEXT_FIELD
  CustomAttributeFieldType.DROPDOWN,
  CustomAttributeFieldType.MULTI_SELECT,
]

/**
 * Filters custom fields to only include those visible to the specified team.
 *
 * The API returns each field with an `allowed_team_ids` array:
 * - Empty array → org-level field, visible to all teams
 * - Non-empty array → team-specific field, visible only to those teams
 *
 * @param {Array} fields - All custom fields from API
 * @param {string|number|null} teamId - ID of the team currently being viewed
 * @returns {Array} Fields visible to the team
 */
/**
 * Filters custom fields by intersection of allowed teams and provided team IDs.
 * Returns fields visible to ANY of the provided teams.
 *
 * @param {Array} fields - All custom fields from API
 * @param {Array} teamIds - Array of team IDs to filter by
 * @returns {Array} Fields visible to at least one of the provided teams
 */
const filterCustomFieldsByTeams = (fields, teamIds) => {
  if (!teamIds || teamIds.length === 0) {
    return fields;
  }

  const teamIdStrings = teamIds.map(String);

  return fields.filter((field) => {
    const allowedTeamIds = field.allowed_team_ids || [];
    // Empty means org-level — visible to everyone
    if (allowedTeamIds.length === 0) return true;
    // Check intersection: field allowed teams ∩ provided team IDs
    return allowedTeamIds.some((id) => teamIdStrings.includes(String(id)));
  });
};

/**
 * Hook to fetch custom fields for Objectives from the API
 *
 * @param {Object} options - Hook options
 * @param {Array} options.teamIds - Optional array of team IDs to filter by
 * @returns {Object} { customFields, customFieldIds, loading, error, allCustomFields }
 */
export const useCustomFields = (options = {}) => {
  const { teamIds = null } = options;
  const [allCustomFields, setAllCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const goalServices = useRef(new GoalServices());

  useEffect(() => {
    let isMounted = true;

    const fetchCustomFields = async () => {
      try {
        setLoading(true);
        const response = await goalServices.current.getGoalCustomFields();

        if (isMounted) {
          const processedFields = response
            .map((field) => {
              // Map TEXT_AREA to TEXT_FIELD for filtering purposes
              // (text area doesn't make sense for filtering - treat as text field)
              if (Number(field.field_type_id) === CustomAttributeFieldType.TEXT_AREA) {
                return { ...field, field_type_id: String(CustomAttributeFieldType.TEXT_FIELD) };
              }
              return field;
            })
            .filter((field) =>
              SUPPORTED_CUSTOM_FIELD_TYPES.includes(Number(field.field_type_id)));

          setAllCustomFields(processedFields);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch custom fields:', err);
          setError(err);
          setAllCustomFields([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCustomFields();

    return () => {
      isMounted = false;
    };
  }, []);

  // Filter fields based on teamIds
  const customFields = useMemo(() =>
    filterCustomFieldsByTeams(allCustomFields, teamIds),
  [allCustomFields, teamIds]);

  const customFieldIds = useMemo(
    () => customFields.map((field) => field.id),
    [customFields]
  );

  return {
    customFields,
    customFieldIds,
    loading,
    error,
    allCustomFields, // Expose unfiltered fields for debugging if needed
  };
};

/**
 * Helper to filter options by search term (case-insensitive contains)
 * Matches RBR's withContainsSearch helper
 */
export const withContainsSearch = (options, term) =>
  options.filter(({ label }) => label.toLowerCase().includes(term.toLowerCase()));

/**
 * Maps a single custom field from the API to a fieldDef for the Filters component
 * Matches RBR's generateCustomFields implementation
 *
 * Valid field types for @workboard/ui-list-filters:
 * - 'text-input-field' (for Text fields - uses contains search)
 * - 'single-select' (for Dropdown fields)
 * - 'multi-select' (for Multi-select fields)
 *
 * @param {Object} field - Custom field from API
 * @param {string} field.name - Field display name
 * @param {string} field.field_type_id - Field type (1=TextField, 3=Dropdown, 4=MultiSelect)
 * @param {Array} field.fieldValues - Options for dropdown/multi-select
 * @param {Array} field.field_values - Options for dropdown/multi-select (alternative key)
 * @returns {Object} Field definition for createFieldDefs
 */
export const mapCustomFieldToFieldDef = (field) => {
  const {
    name, field_type_id, fieldValues, field_values,
  } = field;
  const fieldType = Number(field_type_id);

  // Handle text field type - use 'text-input-field' filter type with contains operator
  if (fieldType === CustomAttributeFieldType.TEXT_FIELD) {
    return {
      label: name,
      type: 'text-input-field',
      operators: ['contains'],
    };
  }

  // Handle dropdown and multi-select field types
  const isMultiSelect = fieldType === CustomAttributeFieldType.MULTI_SELECT;
  const type = isMultiSelect ? 'multi-select' : 'single-select';
  const operators = isMultiSelect ? ['includes'] : ['is'];

  // Build options list from field values
  const optionsSource = fieldValues || field_values || [];
  const optionsList = optionsSource.map((opt) => ({
    label: opt.value,
    value: opt.value,
  }));

  return {
    label: name,
    type,
    operators,
    options: (
      (term) => Promise.resolve(term ? withContainsSearch(optionsList, term) : optionsList)
    ),
  };
};

/**
 * Object type for Objectives (OKR)
 * Used when sending custom field filters to the API
 */
const OBJECTIVE_OBJECT_TYPE = 5;

/** Delimiter for multi-select values. Use \x01 to avoid breaking on commas in option labels. */
const CUSTOM_FIELD_VALUE_DELIMITER = '\x01';

/**
 * Maps Filters component custom field values to API params shape
 *
 * @param {Object} filterValues - The filter values from the Filters component
 * @param {Array} customFieldIds - Array of custom field IDs to look for in criteria
 * @returns {Object} Object with customFields JSON string for API
 *
 * API expects format:
 * customFields=[{"fieldId":123,"value":"some value","operator":"equals","objectType":5}]
 *
 * Operator mapping from UI to API:
 * - 'is' → 'equals' (single-select dropdown)
 * - 'includes' → 'in' (multi-select, match ANY selected value)
 * - 'includes all' → 'includesAll' (multi-select, match ALL selected values)
 */
export const mapCustomFieldsToParams = (filterValues, customFieldIds = []) => {
  if (!customFieldIds.length) {
    return { customFields: undefined };
  }

  const criteria = filterValues?.group?.criteria || [];
  const customFieldFilters = [];

  const operatorMap = {
    is: 'equals',
    includes: 'in',
    'includes all': 'includesAll',
    contains: 'contains', // For text field filtering
  };

  customFieldIds.forEach((fieldId) => {
    const fieldCriteria = criteria.find((c) => c.fieldName === `customField-${fieldId}`);
    if (!fieldCriteria) return;

    const apiOperator = operatorMap[fieldCriteria.operator] || 'equals';
    const { value, values } = fieldCriteria;
    let filterValue;

    if (value !== undefined && value !== null) {
      filterValue = value?.value ?? value;
    } else if (values?.length) {
      filterValue = values.map((v) => v?.value ?? v).join(CUSTOM_FIELD_VALUE_DELIMITER);
    }

    if (filterValue) {
      customFieldFilters.push({
        fieldId,
        value: filterValue,
        operator: apiOperator,
        objectType: OBJECTIVE_OBJECT_TYPE,
      });
    }
  });

  return {
    customFields: customFieldFilters.length
      ? JSON.stringify(customFieldFilters)
      : undefined,
  };
};

/**
 * Validates and builds initial criteria for custom fields from URL or storage
 * Only includes values that exist in the custom field's available options
 * (for dropdown/multi-select), or any non-empty value (for text fields)
 *
 * @param {Array} storedCustomFields - saved/persisted Custom fields to be parsed
 * @param {Array} customFields - Available custom fields from API
 * @returns {Array} Valid criteria for the Filters component
 *
 * Note: multi-select uses `values` (array), single-select/text-input-field uses `value`
 */
export const buildCustomFieldInitialCriteria = (storedCustomFields, validCustomFields) => {
  if (!storedCustomFields?.length || !validCustomFields?.length) {
    return [];
  }

  const criteria = [];
  const uiOperatorMap = {
    equals: 'is',
    in: 'includes',
    includesAll: 'includes all',
    contains: 'contains', // For text field filtering
  };

  storedCustomFields.forEach((storedField) => {
    const { fieldId, value, operator } = storedField;
    // Use loose equality to handle number/string mismatch (JSON vs API response)
    const customField = validCustomFields.find((cf) => String(cf.id) === String(fieldId));
    if (!customField) return;

    const fieldTypeId = Number(customField.field_type_id);
    const isTextField = fieldTypeId === CustomAttributeFieldType.TEXT_FIELD;
    const isMultiSelect = fieldTypeId === CustomAttributeFieldType.MULTI_SELECT;

    // Handle text fields - no option validation needed
    if (isTextField) {
      if (!value || !value.trim()) return;
      criteria.push({
        fieldName: `customField-${fieldId}`,
        fieldType: 'text-input-field',
        operator: uiOperatorMap[operator] || 'contains',
        value: value.trim(),
      });
      return;
    }

    // Handle dropdown and multi-select - validate against available options
    const fieldValues = customField.fieldValues || customField.field_values || [];
    const validOptions = fieldValues.map((opt) => (typeof opt === 'object' ? opt.value : opt));
    const selectedValues = value.split(CUSTOM_FIELD_VALUE_DELIMITER).map((v) => v.trim());
    const validValues = selectedValues.filter((v) => validOptions.includes(v));
    if (!validValues.length) return;

    const fieldType = isMultiSelect ? 'multi-select' : 'single-select';
    const uiOperator = uiOperatorMap[operator] || (isMultiSelect ? 'includes' : 'is');

    if (isMultiSelect) {
      criteria.push({
        fieldName: `customField-${fieldId}`,
        fieldType,
        operator: uiOperator,
        values: validValues.map((v) => ({ label: v, value: v })),
      });
    } else {
      criteria.push({
        fieldName: `customField-${fieldId}`,
        fieldType,
        operator: uiOperator,
        value: { label: validValues[0], value: validValues[0] },
      });
    }
  });

  return criteria;
};
